import { Controller } from "@hotwired/stimulus"
import { seemsOffline } from "../../../../utils/connectivity"

export default class extends Controller {
  static targets = [
    "form", "numberOfItems", "count", "continue", "error",
    "previousCompleted", "name", "nameError", "nameSave", "nameModal", "reviewModal", "summary"
  ]
  static values = { countUrl: String, nameValidateUrl: String }

  // name -> [heading, layout, spacing] for the Review modal summary, in display order
  // compact = 24px (mb-6), loose = 32px (mb-8)
  get summarySections() {
    return [
      ["quiz[custom_courses][]", "Assignments Selected", "list", "compact"],
      ["quiz[todo_list_ids][]", "To-Do Lists Selected", "list", "compact"],
      ["quiz[clinical_disciplines][]", "Clinical Discipline Selected", "list", "compact"],
      ["quiz[body_systems][]", "Body System Selected", "lines", "compact"],
      ["quiz[basic_science_disciplines][]", "Basic Science Disciplines", "lines", "compact"],
      ["quiz[clinical_locations][]", "Clinical Location", "lines", "compact"],
      ["quiz[clinical_excellence][]", "Clinical Excellence Topics", "lines", "compact"],
      ["quiz[clinical_focuses][]", "Clinical Focus", "lines", "loose"],
      ["quiz[include_questions]", "Included", "lines", "loose"],
      ["quiz[include_previously_completed][]", "Previously Completed", "lines", "loose"],
      ["quiz[include_related_to_cases]", "Included Questions", "lines", "loose"]
    ]
  }

  connect() {
    this.boundRefresh = this.scheduleRefresh.bind(this)
    this.formTarget.addEventListener("change", this.boundRefresh)
    this.syncAllSelectAlls()
    this.refreshCount()
  }

  disconnect() {
    this.formTarget.removeEventListener("change", this.boundRefresh)
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
  }

  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => this.refreshCount(), 200)
  }

  buildPayload() {
    const formData = new FormData(this.formTarget)
    const quiz = {}
    formData.forEach((value, key) => {
      const match = key.match(/^quiz\[(.+?)\](\[\])?$/)
      if (!match) return

      const attr = match[1]
      if (match[2]) {
        quiz[attr] = quiz[attr] || []
        quiz[attr].push(value)
      } else {
        quiz[attr] = value
      }
    })
    return { quiz }
  }

  refreshCount() {
    if (!this.hasCountUrlValue) return

    fetch(this.countUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(this.buildPayload())
    })
      .then((response) => response.json())
      .then((data) => this.applyCount(data.count))
      .catch(() => {})
  }

  applyCount(count) {
    const total = parseInt(count, 10) || 0

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${total} questions available`
    }
    if (this.hasNumberOfItemsTarget) {
      this.numberOfItemsTarget.setAttribute("max", total)
    }
    if (this.hasContinueTarget) {
      this.continueTarget.disabled = total === 0
      this.continueTarget.classList.toggle("opacity-50", total === 0)
      this.continueTarget.classList.toggle("pointer-events-none", total === 0)
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.toggle("hidden", total !== 0)
    }
  }

  toggleAll(event) {
    const group = event.target.dataset.generatorSelectAll
    const checked = event.target.checked
    this.formTarget
      .querySelectorAll(`input[data-generator-group="${group}"]`)
      .forEach((checkbox) => { checkbox.checked = checked })
    this.refreshCount()
  }

  syncSelectAll(event) {
    const group = event.target.dataset.generatorGroup
    if (!group) return

    this.updateSelectAll(group)
  }

  syncAllSelectAlls() {
    this.formTarget.querySelectorAll("input[data-generator-select-all]").forEach((selectAll) => {
      this.updateSelectAll(selectAll.dataset.generatorSelectAll)
    })
  }

  updateSelectAll(group) {
    const checkboxes = this.formTarget.querySelectorAll(`input[data-generator-group="${group}"]`)
    const selectAll = this.formTarget.querySelector(`input[data-generator-select-all="${group}"]`)
    if (!selectAll || checkboxes.length === 0) return

    selectAll.checked = Array.from(checkboxes).every((checkbox) => checkbox.checked)
  }

  togglePreviousCompleted(event) {
    if (!this.hasPreviousCompletedTarget) return

    this.previousCompletedTarget.classList.toggle("hidden", event.target.value === "new")
  }

  submitNameStep(event) {
    if (!this.isNameModalOpen()) return

    event.preventDefault()
    const trigger = this.hasNameSaveTarget ? this.nameSaveTarget : event.currentTarget
    const reviewEvent = new Event("click", { bubbles: true, cancelable: true })
    Object.defineProperty(reviewEvent, "currentTarget", { value: trigger })
    Object.defineProperty(reviewEvent, "submitter", { value: trigger })
    void this.goToReview(reviewEvent)
  }

  isNameModalOpen() {
    if (!this.hasNameModalTarget) return false

    const container = this.nameModalTarget.querySelector("[data-railsui-modal-target='container']")
    return Boolean(container && !container.classList.contains("hidden"))
  }

  openNameModal(event) {
    event.preventDefault()
    if (this.blockIfOffline(event.currentTarget)) return

    if (this.hasNumberOfItemsTarget && !this.numberOfItemsTarget.checkValidity()) {
      this.numberOfItemsTarget.reportValidity()
      return
    }
    this.clearNameError()
    this.modalController(this.nameModalTarget)?.open()
  }

  async goToReview(event) {
    event.preventDefault()
    const trigger = event.submitter instanceof HTMLElement ? event.submitter : event.currentTarget
    if (this.blockIfOffline(trigger instanceof HTMLElement ? trigger : null)) return

    const name = this.hasNameTarget ? this.nameTarget.value.trim() : ""
    if (!name) {
      if (this.hasNameTarget) {
        this.nameTarget.setCustomValidity("Please name your quiz")
        this.nameTarget.reportValidity()
      }
      return
    }

    if (this.hasNameTarget) {
      this.nameTarget.setCustomValidity("")
    }

    if (this.hasNameValidateUrlValue) {
      const isValid = await this.validateQuizName(name)
      if (!isValid) return
    }

    this.modalController(this.nameModalTarget)?.close()
    this.buildSummary()
    this.modalController(this.reviewModalTarget)?.open()
  }

  async validateQuizName(name) {
    try {
      const response = await fetch(this.nameValidateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ quiz: { name } })
      })
      const data = await response.json()

      if (data.valid) {
        this.clearNameError()
        return true
      }

      this.showNameError(data.error || "Name has already been taken")
      return false
    } catch {
      if (seemsOffline()) {
        this.blockIfOffline(this.hasNameTarget ? this.nameTarget : null)
        return false
      }
      return true
    }
  }

  blockIfOffline(trigger) {
    if (!seemsOffline() || !trigger) return false

    const nav = this.application.getControllerForElementAndIdentifier(document.body, "offline-navigation")
    if (nav) nav.showFeatureUnavailable(trigger)
    return true
  }

  showNameError(message) {
    if (!this.hasNameTarget) return

    this.nameTarget.classList.add("form-input-error")
    this.nameTarget.setAttribute("aria-invalid", "true")
    this.nameTarget.focus()

    if (!this.hasNameErrorTarget) return

    const errorId = `${this.nameTarget.id}_error`
    this.nameErrorTarget.id = errorId
    this.nameErrorTarget.textContent = message
    this.nameErrorTarget.classList.remove("hidden")

    const describedBy = [
      this.nameTarget.getAttribute("aria-describedby"),
      errorId
    ].filter(Boolean).join(" ")
    this.nameTarget.setAttribute("aria-describedby", describedBy)
  }

  clearNameError() {
    if (!this.hasNameTarget) return

    this.nameTarget.classList.remove("form-input-error")
    this.nameTarget.removeAttribute("aria-invalid")
    this.nameTarget.setCustomValidity("")

    if (!this.hasNameErrorTarget) return

    this.nameErrorTarget.textContent = ""
    this.nameErrorTarget.classList.add("hidden")
    this.nameErrorTarget.removeAttribute("id")

    const describedBy = (this.nameTarget.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter((id) => id && !id.endsWith("_error"))
      .join(" ")

    if (describedBy) {
      this.nameTarget.setAttribute("aria-describedby", describedBy)
    } else {
      this.nameTarget.removeAttribute("aria-describedby")
    }
  }

  buildSummary() {
    if (!this.hasSummaryTarget) return

    const blocks = []
    this.summarySections.forEach(([name, heading, layout, spacing]) => {
      const labels = this.checkedLabels(name)
      if (!labels.length) return
      blocks.push(this.summaryBlock(heading, labels, layout, spacing))
    })

    if (this.hasNumberOfItemsTarget && this.numberOfItemsTarget.value) {
      blocks.push(this.summaryBlock("Questions", [this.numberOfItemsTarget.value], "lines", "loose"))
    }

    this.summaryTarget.innerHTML = blocks.join("")

    const last = this.summaryTarget.lastElementChild
    if (last) {
      last.classList.remove("mb-6")
      last.classList.add("mb-8")
    }
  }

  summaryBlock(heading, labels, layout, spacing = "loose") {
    const marginClass = spacing === "compact" ? "mb-6" : "mb-8"
    const body =
      layout === "list"
        ? `<ul class="mt-2 list-disc pl-5 text-[14px]">${labels.map((l) => `<li>${this.escape(l)}</li>`).join("")}</ul>`
        : labels.map((l) => `<p class="mt-2 text-[14px]">${this.escape(l)}</p>`).join("")

    return `<div class="${marginClass}"><p class="type-h3 !text-[16px]">${this.escape(heading)}</p>${body}</div>`
  }

  checkedLabels(name) {
    const labels = []
    this.formTarget.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      if (!input.checked) return
      const label = this.formTarget.querySelector(`label[for="${input.id}"]`)
      labels.push(label ? label.textContent.trim() : input.value)
    })
    return labels
  }

  modalController(wrapper) {
    if (!wrapper) return null

    const element = wrapper.querySelector('[data-controller~="railsui-modal"]')
    return element ? this.application.getControllerForElementAndIdentifier(element, "railsui-modal") : null
  }

  escape(value) {
    const node = document.createElement("div")
    node.textContent = value == null ? "" : String(value)
    return node.innerHTML
  }
}
